import { useState, useEffect } from "react";
import {
  Flex,
  NavLink,
  Image,
  Box,
  MenuButton,
  get,
  ThemeUIStyleObject,
} from "theme-ui";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { keyframes } from "@emotion/react";
import { useTranslation } from "react-i18next";

import { RoutesKeys, Routes } from "src/constants";
import { NavbarProps } from "./Navbar.d";
import logo from "src/assets/images/logo_color.png";
import { ROUTES } from "src/constants";
import { extractBaseLang } from "src/utils";

export const Navbar: React.FC<NavbarProps> = ({
  activeRoute = ROUTES.terms,
  onNavClick,
  onDownloadClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [offset, setOffSet] = useState(0);
  const index = useBreakpointIndex();

  const { t, i18n } = useTranslation();
  const currentLanguage = extractBaseLang(i18n.language);

  const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
  const handleNavigate = (route: Routes[RoutesKeys]) => {
    onNavClick(route);
    if (index < 2) setIsOpen(false);
  };

  useEffect(() => {
    window.onscroll = () => {
      setOffSet(window.pageYOffset);
    };
  }, []);

  const activeStyles = (
    route: Routes[RoutesKeys],
    bg = "primary"
  ): ThemeUIStyleObject => {
    const isActive = activeRoute === `/${route}`;
    return {
      fontWeight: isActive ? "bold" : "none",
      "&:after": {
        content: '""',
        position: "absolute",
        width: isActive ? "100%" : "0%",
        left: isActive ? 0 : "50%",
        height: "3px",
        bg,
        top: "32px",
        margin: "0 auto",
        transition: "width .1s ease-in-out, left .1s ease-in-out",
      },
    };
  };

  useEffect(() => {
    setIsOpen(index > 1);
  }, [index]);
  return (
    <Box
      style={{
        zIndex: 5,
        position: "sticky",
        top: offset > 5 ? -5 : 0,
        transition: "top .1s ease .1s",
      }}
      sx={{ boxShadow: offset > 5 ? 0 : "none" }}
    >
      <Flex
        as="nav"
        sx={{
          bg: "background",
          height: [2, 4],
          alignItems: "center",
          justifyContent: "center",
          px: [3, 4, 5],
        }}
      >
        <NavLink onClick={() => onNavClick(ROUTES.home)} p={2}>
          <Image
            src={logo}
            alt="vecinos"
            sx={{
              width: ["6.25rem", "8.75rem"],
              height: ["1.2rem", "1.7rem"],
              marginTop: ["10px", "10px", "0px"],
            }}
          />
        </NavLink>
        <MenuButton
          variant="icon"
          sx={{
            ml: "auto",
            display: ["block", "block", "none"],
          }}
          onClick={() => setIsOpen(!isOpen)}
        />
        <Box
          sx={{
            animation: `${fadeIn} 0.5s`,
            ml: "auto",
            bg: "background",
            alignItems: "center",
            left: 0,
            flexDirection: ["column", "column", "row"],
            position: ["absolute", "absolute", "relative"],
            width: ["100%", "100%", "auto"],
            zIndex: [1],
            boxShadow: [0, 0, "none"],
            pt: [3, 3, 0],
            pb: [5, 5, 0],
            top: (theme) => [get(theme, "sizes.2"), get(theme, "sizes.4"), 0],
          }}
          style={{ display: isOpen ? "flex" : "none" }}
        >
          {currentLanguage === "es" && (
            <NavLink
              onClick={() => onDownloadClick?.()}
              sx={{
                color: "secondary",
                my: 3,
                display: ["inline-block", "inline-block", "none"],
                "&:hover": {
                  color: "secondary",
                },
              }}
            >
              {t("navigation.download_app")}
            </NavLink>
          )}
          <NavLink
            onClick={() => handleNavigate(ROUTES.answers)}
            sx={{
              position: "relative",
              my: 3,
              ml: [0, 0, 6],
              ...activeStyles(ROUTES.answers),
            }}
          >
            {t("navigation.faqs")}
          </NavLink>
          {currentLanguage === "es" && (
            <NavLink
              onClick={() => handleNavigate(ROUTES.terms)}
              sx={{
                position: "relative",
                my: 3,
                ml: [0, 0, 6],
                ...activeStyles(ROUTES.terms),
              }}
            >
              {t("navigation.terms_and_conds")}
            </NavLink>
          )}
          <NavLink
            onClick={() => handleNavigate(ROUTES.contact)}
            sx={{
              position: "relative",
              my: 3,
              color: ["primary", "primary", "secondary"],
              ml: [0, 0, 6],
              "&:hover": {
                color: ["primary", "primary", "secondary"],
              },
              ...activeStyles(
                ROUTES.contact,
                index > 1 ? "secondary" : "primary"
              ),
            }}
          >
            {t("navigation.contact")}
          </NavLink>
        </Box>
      </Flex>
    </Box>
  );
};
